import type { FertilityTreatmentsState } from "./FertilityTreatmentsContext";
import type {
  CreateCycleInput,
  UpdateCycleBody,
} from "@src/packages/patients/hooks/usePatient";
import type { FETHistoricalRecordInput } from "./useFETHistoricalRecords";

type Treatment = FertilityTreatmentsState["treatments"][number];

export function hasHistoricalTreatmentDetails<T extends object>(
  treatment: T,
): boolean {
  return Object.values(treatment).some(
    (value) => value !== null && value !== undefined && value !== "",
  );
}
type CyclePhaseInput = Exclude<
  NonNullable<CreateCycleInput["phases"]>[number],
  { op: "delete" }
>;
type FertilityMetricInput = NonNullable<
  CyclePhaseInput["fertilityMetrics"]
>[number];

export function parseTreatmentNotes(notes: string | null | undefined): {
  clinic: string | null;
  outcome: string | null;
  embryosFrozen: string | null;
  remainingNotes: string | null;
} {
  if (!notes) {
    return {
      clinic: null,
      outcome: null,
      embryosFrozen: null,
      remainingNotes: null,
    };
  }

  let clinic: string | null = null;
  let outcome: string | null = null;
  let embryosFrozen: string | null = null;
  const parts: string[] = [];

  for (const segment of notes
    .split(";")
    .map((s) => s.trim())
    .filter(Boolean)) {
    const clinicMatch = segment.match(/^clinic\s*:\s*(.*)$/i);
    const outcomeMatch = segment.match(/^outcome\s*:\s*(.*)$/i);
    const embryosFrozenMatch = segment.match(/^embryos_frozen\s*:\s*(.*)$/i);

    if (clinicMatch) {
      clinic = clinicMatch[1]?.trim() || null;
    } else if (outcomeMatch) {
      outcome = outcomeMatch[1]?.trim() || null;
    } else if (embryosFrozenMatch) {
      embryosFrozen = embryosFrozenMatch[1]?.trim() || null;
    } else {
      parts.push(segment);
    }
  }

  return {
    clinic: clinic || null,
    outcome: outcome || null,
    embryosFrozen: embryosFrozen || null,
    remainingNotes: parts.length > 0 ? parts.join("; ") : null,
  };
}

export function mapTreatmentTypeToCycleType(
  type: string | null | undefined,
):
  | "ivf"
  | "iui"
  | "fet"
  | "icsi"
  | "oi"
  | "egg_freezing"
  | "embryo_freezing"
  | "sperm_freezing"
  | null {
  if (!type) return null;

  const typeMap: Record<
    string,
    | "ivf"
    | "iui"
    | "fet"
    | "icsi"
    | "oi"
    | "egg_freezing"
    | "embryo_freezing"
    | "sperm_freezing"
  > = {
    ivf: "ivf",
    iui: "iui",
    fet: "fet",
    icsi: "icsi",
    oi: "oi",
    egg_freezing: "egg_freezing",
    embryo_freezing: "embryo_freezing",
    sperm_freezing: "sperm_freezing",
  };

  return typeMap[type.toLowerCase()] || null;
}

export function mapOutcomeToEnum(
  outcome: string | null | undefined,
): "cancelled" | "pregnancy" | "no_pregnancy" | "miscarriage" | "other" | null {
  if (!outcome) return null;

  const outcomeMap: Record<
    string,
    "cancelled" | "pregnancy" | "no_pregnancy" | "miscarriage" | "other"
  > = {
    miscarriage: "miscarriage",
    pregnancy: "pregnancy",
    other: "other",
    cancelled: "cancelled",
    no_pregnancy: "no_pregnancy",
  };

  return outcomeMap[outcome.toLowerCase()] || null;
}

type HistoricalOutcome = NonNullable<CreateCycleInput["outcome"]>;

/**
 * Preserve the exact known outcome in the structured Cycle field. Generic
 * pregnancy is deliberately not upgraded to live birth, and adverse subtype
 * values are not collapsed into an undifferentiated miscarriage.
 */
export function mapOutcomeToStructuredOutcome(
  outcome: string | null | undefined,
): HistoricalOutcome | null {
  if (!outcome) return null;

  switch (outcome.toLowerCase()) {
    case "pregnancy":
      return { type: "pregnancy" };
    case "live_birth":
      return { type: "pregnancy", pregnancyOutcome: "live_birth" };
    case "multiple_live_births":
      return { type: "pregnancy", pregnancyOutcome: "multiple_live_birth" };
    case "no_pregnancy":
      return { type: "no_pregnancy" };
    case "cancelled":
      return { type: "cancelled" };
    case "chemical_pregnancy":
    case "ectopic_pregnancy":
    case "still_birth":
    case "complete_miscarriage":
    case "incomplete_miscarriage":
    case "missed_miscarriage":
    case "recurrent_miscarriage":
    case "anembryonic_pregnancy":
      return {
        type: "miscarriage",
        miscarriageOutcome: outcome.toLowerCase() as NonNullable<
          HistoricalOutcome["miscarriageOutcome"]
        >,
      };
    case "miscarriage":
      return { type: "miscarriage" };
    case "other":
      return { type: "other" };
    default:
      return null;
  }
}

export type CycleOperation =
  | { type: "delete"; id: string }
  | {
      type: "create";
      data: CreateCycleInput;
      fetHistory?: FETHistoricalRecordDraft;
      treatmentIndex: number;
    }
  | {
      type: "update";
      cycleId: string;
      data: UpdateCycleBody;
      treatmentIndex: number;
      fetHistory?: FETHistoricalRecordDraft;
    };

export type FETHistoricalRecordDraft = Omit<
  FETHistoricalRecordInput,
  "cycleId" | "clientRequestId"
> & {
  clientRequestId?: string;
  historyRecordId?: string;
};

function mapTreatmentOutcomeToFETResult(
  outcome: string | null | undefined,
): NonNullable<FETHistoricalRecordDraft["resultState"]> {
  switch (outcome?.toLowerCase()) {
    case "chemical_pregnancy":
      return "biochemical_pregnancy";
    case "ectopic_pregnancy":
      return "ectopic_pregnancy";
    case "live_birth":
    case "multiple_live_births":
      return "live_birth";
    case "still_birth":
      return "stillbirth";
    case "miscarriage":
    case "complete_miscarriage":
    case "incomplete_miscarriage":
    case "missed_miscarriage":
    case "recurrent_miscarriage":
    case "anembryonic_pregnancy":
      return "early_pregnancy_loss";
    default:
      return "unknown";
  }
}

function parseEmbryoCount(value: string | null | undefined): number | null {
  const normalized = value?.trim() ?? "";
  if (!/^\d+$/.test(normalized)) return null;

  const count = Number(normalized);
  return Number.isSafeInteger(count) ? count : null;
}

function buildEmbryoFrozenPhase(
  treatment: Treatment,
  value: number | null,
): CyclePhaseInput | null {
  if (value === null && !treatment.embryoFrozenMetricId) return null;

  const metric: FertilityMetricInput =
    value === null && treatment.embryoFrozenMetricId
      ? { id: treatment.embryoFrozenMetricId, op: "delete" }
      : {
          ...(treatment.embryoFrozenMetricId
            ? { id: treatment.embryoFrozenMetricId }
            : {}),
          type: "frozen_embryo",
          value,
        };

  return {
    ...(treatment.embryoFrozenPhaseId
      ? { id: treatment.embryoFrozenPhaseId }
      : {}),
    type: "embryo_development",
    ...(treatment.date ? { startDate: treatment.date } : {}),
    fertilityMetrics: [metric],
  };
}

export function transformFertilityTreatmentsToOperations(
  state: FertilityTreatmentsState,
): CycleOperation[] {
  const operations: CycleOperation[] = [];
  const idsToDeleteSet = new Set(state.treatmentIdsToDelete || []);

  state.treatmentIdsToDelete.forEach((id) => {
    if (id) operations.push({ type: "delete", id });
  });

  state.treatments.forEach((t, treatmentIndex) => {
    if (t.id && idsToDeleteSet.has(t.id)) return;

    const {
      clinic: legacyClinic,
      outcome,
      embryosFrozen: legacyEmbryosFrozen,
      remainingNotes,
    } = parseTreatmentNotes(t.notes);
    const cycleType = mapTreatmentTypeToCycleType(t.type);

    const cycleData: CreateCycleInput = {
      ...(cycleType ? { cycleType } : {}),
      status: "closed",
      rawCycleType: t.type?.toLowerCase(),
    };

    cycleData.description = t.name ?? null;
    cycleData.startDate = t.date ?? null;
    cycleData.clinic = t.clinic ?? legacyClinic;
    const provider = t.provider ?? legacyClinic;
    if (provider) cycleData.provider = provider;

    const structuredOutcome = mapOutcomeToStructuredOutcome(
      t.outcome ?? outcome,
    );
    if (structuredOutcome) cycleData.outcome = structuredOutcome;
    if (remainingNotes) {
      cycleData.dayNotes = [{ date: t.date ?? null, notes: remainingNotes }];
    }

    const embryosFrozen =
      t.embryosFrozen !== undefined
        ? t.embryosFrozen
        : parseEmbryoCount(legacyEmbryosFrozen);
    if (cycleType === "embryo_freezing") {
      const frozenPhase = buildEmbryoFrozenPhase(t, embryosFrozen);
      if (frozenPhase) cycleData.phases = [frozenPhase];
    }

    const clinicName = t.clinic ?? legacyClinic;
    const fetHistory: FETHistoricalRecordDraft | undefined =
      cycleType === "fet"
        ? {
            ...(t.historyRecordId
              ? { historyRecordId: t.historyRecordId }
              : {}),
            ...(t.historyClientRequestId
              ? { clientRequestId: t.historyClientRequestId }
              : {}),
            protocolClass: t.protocolClass ?? "unknown",
            transferState: t.transferState ?? "unknown",
            embryoIdentity:
              t.embryoCount === null || t.embryoCount === undefined
                ? "unknown"
                : "count_only",
            ...(t.embryoCount === null || t.embryoCount === undefined
              ? {}
              : { embryoCount: t.embryoCount }),
            thawState: t.thawState ?? "unknown",
            resultState:
              t.resultState ??
              mapTreatmentOutcomeToFETResult(t.outcome ?? outcome),
            startedAt: t.date ?? null,
            ...(clinicName ? { clinicName } : {}),
            sourceKind: "patient_reported",
            ...(remainingNotes ? { sourceOriginalNotes: remainingNotes } : {}),
          }
        : undefined;

    if (t.id) {
      const operation: Extract<CycleOperation, { type: "update" }> = {
        type: "update",
        cycleId: t.id,
        data: cycleData,
        treatmentIndex,
      };
      if (fetHistory) operation.fetHistory = fetHistory;
      operations.push(operation);
    } else {
      const operation: Extract<CycleOperation, { type: "create" }> = {
        type: "create",
        data: cycleData,
        treatmentIndex,
      };
      if (fetHistory) operation.fetHistory = fetHistory;
      operations.push(operation);
    }
  });

  return operations;
}
